import { Pagination } from "@mui/material";
import { UploadIcon } from "@radix-ui/react-icons";
import { ChevronLeft, MoreHorizontal } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import modelCommune from "../../assets/model-fichier/model_commune.xlsx";
import { Loader } from "../../components/Widget/Loader";
import { store } from "../../services/service";
import { useStoreCommune } from "../../store/storeCommune";
import {
  AJOUTER_COMMUNE,
  CHARGER_FICHIER_COMMUNE,
  LISTE_COMMUNE_PAR_PAGE,
  MODIFIER_ETAT_COMMUNE,
} from "../../Utils/constant";
import { Quartier } from "./Quartier";

const etatConfig = {
  ACTIF: { label: "Actif", cls: "bg-green-100 text-green-700" },
  INACTIF: { label: "Inactif", cls: "bg-yellow-100 text-yellow-700" },
  SUPPRIME: { label: "Supprimé", cls: "bg-red-100 text-red-700" },
};

const EtatBadge = ({ etat }) => {
  const cfg = etatConfig[etat] || {
    label: etat,
    cls: "bg-gray-100 text-gray-600",
  };
  return (
    <span
      className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
};

export const Commune = () => {
  const [isSubmit, setIsSubmit] = useState(false);
  const communes = useStoreCommune();
  const [message, setMessage] = useState("");
  const [fichier, setFichier] = useState([]);
  const [item, setItem] = useState(null);
  const [selectItem, setSelectItem] = useState(null);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();

  const handleChangeFichier = (e) => {
    const newFiles = Array.from(e.target.files);
    setFichier((prev) => [...prev, ...newFiles]);
  };

  useEffect(() => {
    communes.getData(`${LISTE_COMMUNE_PAR_PAGE}?page=0&param=&size=10`);
  }, []);

  useEffect(() => {
    if (selectItem) {
      setValue("designation", selectItem.designation);
      setValue("etat", selectItem.etat || "ACTIF");
      setValue("distancePbo", selectItem.distancePbo || "");
    }
  }, [selectItem, setValue]);

  const more = (page) => {
    communes.getData(
      `${LISTE_COMMUNE_PAR_PAGE}?page=${page - 1}&param=&size=10`,
    );
  };

  const submit = (data) => {
    setIsSubmit(true);
    store(AJOUTER_COMMUNE, "application/json", { ...data, id: selectItem?.id })
      .then((res) => {
        setIsSubmit(false);
        if (res.status === 200) {
          reset();
          setSelectItem(null);
          communes.getData(`${LISTE_COMMUNE_PAR_PAGE}?page=0&param=&size=10`);
          document.getElementById("update").close();
          toast.success("Mise à jour réussie");
        }
      })
      .catch((err) => {
        setIsSubmit(false);
        toast.error(err.response.data.donnee);
      });
  };

  const inputCls =
    "w-full h-10 px-3 border border-gray-200 rounded-lg text-sm text-gray-900 bg-gray-50 outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 transition";
  const selectCls =
    "w-full h-10 px-3 border border-gray-200 rounded-lg text-sm text-gray-900 bg-gray-50 outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 transition";
  const submitBtnCls = (loading) =>
    `w-full h-10 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition ${
      loading
        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
        : "bg-yellow-400 hover:bg-yellow-300 text-gray-900"
    }`;
  const closeBtnCls =
    "w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 text-sm font-semibold transition";

  /* ── Quartier detail view ── */
  if (item !== null) {
    return (
      <div className="my-3">
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => setItem(null)}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition"
          >
            <ChevronLeft size={16} />
          </button>
          <p className="text-base font-semibold text-gray-900">
            {item?.designation}
          </p>
          <EtatBadge etat={item?.etat} />
        </div>
        <Quartier id={item?.id} />
      </div>
    );
  }

  /* ── Main list view ── */
  return (
    <div className="my-3">
      {/* Header */}
      <div className="mb-1">
        <p className="text-2xl font-bold text-gray-900 tracking-tight">
          Communes
        </p>
        <p className="text-sm text-gray-400 mt-1">
          Gestion des communes et de leurs quartiers
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 py-4 border-b border-gray-100 mb-6">
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 h-10">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9ca3af"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            onChange={(e) =>
              communes.getData(
                `${LISTE_COMMUNE_PAR_PAGE}?page=0&param=${e.target.value}&size=10`,
              )
            }
            type="text"
            placeholder="Rechercher une commune…"
            className="bg-transparent border-none outline-none text-sm text-gray-900 placeholder-gray-400 w-48"
          />
        </div>

        <button
          onClick={() => document.getElementById("upload").showModal()}
          className="ml-auto h-10 px-4 flex items-center gap-2 bg-gray-900 hover:bg-gray-700 text-white text-sm font-medium rounded-xl transition"
        >
          <UploadIcon />
          Charger un fichier
        </button>
      </div>

      {/* ── Modals ── */}

      {/* Update modal */}
      <dialog id="update" className="modal">
        <div className="modal-box rounded-2xl p-6 max-w-md">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-900">
              Modifier la commune
            </h2>
            <form method="dialog">
              <button id="fermer_create_trajet" className={closeBtnCls}>
                ✕
              </button>
            </form>
          </div>
          <form onSubmit={handleSubmit(submit)} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Désignation <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={inputCls}
                {...register("designation", { required: true })}
              />
              {errors.designation && (
                <p className="text-xs text-red-500 mt-1">
                  Ce champ est obligatoire
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Distance PBO(en mètres) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={inputCls}
                {...register("distancePbo", { required: true })}
              />
              {errors.distancePbo && (
                <p className="text-xs text-red-500 mt-1">
                  Ce champ est obligatoire
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={isSubmit}
              className={submitBtnCls(isSubmit)}
            >
              {isSubmit ? (
                <>
                  <span className="loading loading-spinner loading-xs" />{" "}
                  Enregistrement…
                </>
              ) : (
                "Enregistrer"
              )}
            </button>
          </form>
        </div>
      </dialog>

      {/* Change status modal */}
      <dialog id="change_status" className="modal">
        <div className="modal-box rounded-2xl p-6 max-w-md">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-900">Modifier l'état</h2>
            <form method="dialog">
              <button id="close_change_status" className={closeBtnCls}>
                ✕
              </button>
            </form>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mb-4">
            <p className="text-xs text-gray-500">Commune</p>
            <p className="text-sm font-semibold text-gray-900 mt-0.5">
              {selectItem?.designation}
            </p>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Nouvel état <span className="text-red-500">*</span>
              </label>
              <select
                className={selectCls}
                {...register("etat", { required: true })}
              >
                <option value="">Sélectionner un état</option>
                <option value="ACTIF">Actif</option>
                <option value="INACTIF">Inactif</option>
                <option value="SUPPRIME">Supprimé</option>
              </select>
              {errors.etat && (
                <p className="text-xs text-red-500 mt-1">
                  Ce champ est obligatoire
                </p>
              )}
            </div>
            <button
              onClick={handleSubmit((data) => {
                setIsSubmit(true);
                store(
                  MODIFIER_ETAT_COMMUNE +
                    `/${selectItem?.id}?etat=${data.etat}`,
                  "application/json",
                  {},
                )
                  .then(() => {
                    setIsSubmit(false);
                    reset();
                    setSelectItem(null);
                    communes.getData(
                      `${LISTE_COMMUNE_PAR_PAGE}?page=0&param=&size=10`,
                    );
                    document.getElementById("close_change_status").click();
                    toast.success("État modifié avec succès");
                  })
                  .catch((err) => {
                    setIsSubmit(false);
                    toast.error(err.response.data.donnee);
                  });
              })}
              disabled={isSubmit}
              className={submitBtnCls(isSubmit)}
            >
              {isSubmit ? (
                <>
                  <span className="loading loading-spinner loading-xs" />{" "}
                  Modification…
                </>
              ) : (
                "Modifier l'état"
              )}
            </button>
          </div>
        </div>
      </dialog>

      {/* Upload modal */}
      <dialog id="upload" className="modal">
        <div className="modal-box rounded-2xl p-6 max-w-lg">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-900">
              Charger un fichier
            </h2>
            <form method="dialog">
              <button id="close_upload" className={closeBtnCls}>
                ✕
              </button>
            </form>
          </div>

          {message && <p className="text-xs text-red-500 mb-3">{message}</p>}

          <label className="block w-full cursor-pointer">
            <div className="border-2 border-dashed border-gray-200 bg-gray-50 rounded-xl p-8 flex flex-col items-center gap-3 hover:border-yellow-400 hover:bg-yellow-50 transition">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-400">
                <UploadIcon />
              </div>
              <p className="text-sm text-gray-500 font-medium">
                {fichier.length > 0 ? (
                  <span className="text-yellow-600 font-semibold">
                    {fichier[0]?.name}
                  </span>
                ) : (
                  "Glisser ou cliquer pour charger"
                )}
              </p>
              <p className="text-xs text-gray-400">.xlsx ou .csv uniquement</p>
              <input
                type="file"
                onChange={handleChangeFichier}
                className="hidden"
              />
            </div>
          </label>

          <div className="flex items-center gap-2 mt-4">
            <a
              href={modelCommune}
              download="modelCommune"
              className="h-9 px-4 flex items-center gap-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition"
            >
              Télécharger le modèle
            </a>
            <button
              onClick={() => {
                setIsSubmit(true);
                store(CHARGER_FICHIER_COMMUNE, "multipart/form-data", {
                  liste: fichier[0],
                })
                  .then((res) => {
                    if (res.status === 200) {
                      setIsSubmit(null);
                      document.getElementById("close_upload").click();
                      communes.getData(
                        `${LISTE_COMMUNE_PAR_PAGE}?page=0&param=&size=10`,
                      );
                      toast.success("Fichier chargé avec succès");
                    }
                  })
                  .catch((err) => {
                    setIsSubmit(false);
                    setMessage(err?.response?.data?.donnee);
                  });
              }}
              disabled={isSubmit || fichier.length === 0}
              className={`ml-auto h-9 px-4 rounded-lg text-xs font-semibold flex items-center gap-2 transition ${
                isSubmit || fichier.length === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-yellow-400 hover:bg-yellow-300 text-gray-900"
              }`}
            >
              {isSubmit ? (
                <>
                  <span className="loading loading-spinner loading-xs" />{" "}
                  Chargement…
                </>
              ) : (
                "Charger le fichier"
              )}
            </button>
          </div>
        </div>
      </dialog>

      {/* Grid */}
      {communes?.loading ? (
        <Loader />
      ) : communes?.data?.data?.length === 0 ? (
        <div className="flex items-center justify-center h-48">
          <p className="text-sm text-gray-400">Aucune commune trouvée</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {communes?.data?.data?.map((item) => (
            <div
              key={item.id}
              className="relative bg-white border border-gray-200 rounded-xl p-3 flex items-start gap-3 hover:shadow-md hover:border-yellow-300 transition-all duration-200"
            >
              {/* Menu */}
              <div className="dropdown dropdown-end absolute top-2 right-2">
                <div
                  tabIndex={0}
                  role="button"
                  className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-500 transition cursor-pointer"
                >
                  <MoreHorizontal size={15} />
                </div>
                <ul
                  tabIndex={0}
                  className="dropdown-content z-10 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg w-44 p-1.5"
                >
                  <li>
                    <button
                      className="w-full h-8 px-3 text-left text-xs font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition"
                      onClick={() => setItem(item)}
                    >
                      Voir les quartiers
                    </button>
                  </li>
                  <li>
                    <button
                      className="w-full h-8 px-3 text-left text-xs font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition"
                      onClick={() => {
                        setSelectItem(item);
                        document.getElementById("update").showModal();
                      }}
                    >
                      Modifier
                    </button>
                  </li>
                  <li>
                    <button
                      className="w-full h-8 px-3 text-left text-xs font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition"
                      onClick={() => {
                        setSelectItem(item);
                        document.getElementById("change_status").showModal();
                      }}
                    >
                      Modifier l'état
                    </button>
                  </li>
                </ul>
              </div>

              {/* Icon */}
              <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center shrink-0 text-amber-500 font-bold text-sm">
                {item.designation?.[0]?.toUpperCase()}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0 pt-0.5 pr-6">
                <p className="text-xs font-semibold text-gray-900 truncate">
                  {item.designation}
                </p>
                {item.distancePbo && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {item.distancePbo} mètre(s)
                  </p>
                )}
                {item.typeEtat && (
                  <div className="mt-1.5">
                    <EtatBadge etat={item.typeEtat} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-end mt-6">
        <Pagination
          onChange={(_, v) => more(v)}
          count={communes?.data?.totalPages}
          variant="outlined"
          color="primary"
          shape="rounded"
          size="small"
        />
      </div>
    </div>
  );
};
